
import { useEffect, useState } from 'react'

import { scenariosSelectors } from '@/entities/schedules'
import { useTour } from '@/shared/components/DriverTour/libs/useTour'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { SelectOption } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { Alert, AlertTitle } from '@mui/material'

import { getImportFormDefinition } from './libs/getImportFormDefinition'
import { StyledContainer, StyledContent, StyledTitle } from './StyledImportWizardPage'
import { setupImportWizard } from './thunks/setupImportWizard'
import { ImportFormData, ImportResponse, uploadImportFile } from './thunks/uploadImportFile'

export type ImportWizardPageProps = {
  //
}

export const ImportWizardPage: React.FC<ImportWizardPageProps> = () => {
  const [importResult, setImportResult] = useState<ImportResponse | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const dispatch = useAppDispatch()
  const scenarios = useAppSelector(state => scenariosSelectors.selectAll(state))
  const { isComplete } = useAppSelector(state => state.loader) // Loader state

  const importFormDefinition = getImportFormDefinition({
    defaultValues: {},
    selectOptions: {
      'scenario': scenarios.map((scenario) => ({ label: scenario.name, value: scenario.id })),
    },
  })

  // Setup import page
  useEffect(() => {
    dispatch(setupImportWizard())
  }, [activeGroupId, dispatch])

  // Start tour if not completed
  useTour('import', isComplete)

  const handleSubmit = async (data: Record<string, unknown>) => {
    const selectedScenario = data?.scenario as SelectOption

    const formData: ImportFormData = {
      importTarget: data.importTarget as ImportFormData['importTarget'],
      uploadFile: data.uploadFile as ImportFormData['uploadFile'],
      selectedVersion: '0',
      scenarioId: selectedScenario?.value as ImportFormData['scenarioId'],
      dateOfOperation: data.dateOfOperation as ImportFormData['dateOfOperation'],
      scenarioName: data.scenarioName as ImportFormData['scenarioName'],
      dayOfWeek: data.dayOfWeek as ImportFormData['dayOfWeek'],
      depotTarget: '0',
    }

    setIsUploading(true)
    setImportResult(null)

    try {
      const result = await dispatch(uploadImportFile(formData)).unwrap()

      setImportResult(result || null)
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <StyledContainer>
      <StyledTitle variant="h1">Import Wizard</StyledTitle>
      <StyledContent id="order-import-form" maxWidth="sm">
        <DynamicForm
          {...importFormDefinition}
          onSubmit={handleSubmit}
          slotProps={{
            submitButton: {
              disabled: isUploading,
            },
          }}
        />
        {importResult && (
          <Alert
            severity={importResult.hasErrors ? 'error' : 'success'}
            sx={{ mt: 2 }}
          >
            <AlertTitle>
              {importResult.hasErrors ? 'Upload Errors' : `Upload Finished for File: ${importResult.fileName}`}
            </AlertTitle>

            {!importResult.hasErrors && (
              <>
                <div>{importResult.ordersTotal} Orders Uploaded</div>
                <div>{importResult.ordersAssigned} Orders Assigned to Runs</div>
                <div>{importResult.ordersUnassigned} Unassigned Orders</div>
                <div>{importResult.vehiclesUsed} Number of Runs Used</div>

                {importResult.ungeocodedLocations > 0 && (
                  <div>{importResult.ungeocodedLocations} Ungeocoded Locations</div>
                )}
              </>
            )}

            {importResult.messages.map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </Alert>
        )}

      </StyledContent>
    </StyledContainer>
  )
}

